import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import React, { useEffect, useRef } from "react";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { Button } from "@progress/kendo-react-buttons";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { LoadingPanel } from "../../components/layout/Loading";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  createPaymentReceipt,
  findAllActivePaymentReceipt,
  getAllPaymentReceipt,
  getPaymentReceiptById,
  updatePaymentReceipt,
} from "./services/paymentreceipt.services";
import {
  clearPaymentReceiptDetails,
  closePaymentReceiptDrawerOpen,
} from "./paymentreceiptSlice";
import { Typography } from "@progress/kendo-react-common";
import { getAllKarigar } from "../karigar/services/karigar.services";
import FormDatePicker from "../../components/formFields/FormDateField";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { IKarigar } from "../karigar/karigarModel";

const CreatePaymentReceipt: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  // const PriorityList = useAppSelector((state) => state.priority.PriorityList);
  const PaymentReceiptDetail = useAppSelector(
    (state) => state.paymentreceipt.PaymentReceiptDetail
  );
  const KarigarList = useAppSelector((state) => state.karigar.KarigarList);

  const payment_guid = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector(
    (state) => state.paymentreceipt.formLoading
  );
  const isIndirect = useAppSelector((state) => state.muidrawer.isIndirect);
  const paymentreceiptDrawerOpen = useAppSelector(
    (state) => state.paymentreceipt.paymentreceiptDrawerOpen
  );
  useEffect(() => {
    dispatch(getAllKarigar());
    return () => {
      dispatch(clearPaymentReceiptDetails());
      if (paymentreceiptDrawerOpen) {
        dispatch(closePaymentReceiptDrawerOpen());
      } else {
        dispatch(closed());
      }
    };
  }, []);

  useEffect(() => {
    if (payment_guid) {
      dispatch(getPaymentReceiptById(payment_guid));
    }
  }, [payment_guid]);

  /* This Function calls when Form submit button Clicked */
  const handleSubmit = async (values: any) => {
    try {
      if (!payment_guid) {
        const InsertPayload = {
          receipt_date: values?.receipt_date,
          karigar_id: values?.karigar_id,
          amount: values?.amount,
          remarks: values?.remarks,
        };
        const response = await dispatch(createPaymentReceipt(InsertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (paymentreceiptDrawerOpen) {
            dispatch(closePaymentReceiptDrawerOpen());
          } else {
            dispatch(closed());
          }
          dispatch(clearPaymentReceiptDetails());
          isIndirect || paymentreceiptDrawerOpen
            ? dispatch(findAllActivePaymentReceipt())
            : dispatch(getAllPaymentReceipt(0));
        }
      } else {
        const editPayload = {
          payment_guid: payment_guid,
          receipt_date: values?.receipt_date,
          karigar_id: values?.karigar_id,
          amount: values?.amount,
          remarks: values?.remarks,
        };
        const response = await dispatch(updatePaymentReceipt(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (paymentreceiptDrawerOpen) {
            dispatch(closePaymentReceiptDrawerOpen());
          } else {
            dispatch(closed());
          }
          dispatch(clearPaymentReceiptDetails());
          isIndirect || paymentreceiptDrawerOpen
            ? dispatch(findAllActivePaymentReceipt())
            : dispatch(getAllPaymentReceipt(0));
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    if (paymentreceiptDrawerOpen) {
      dispatch(closePaymentReceiptDrawerOpen());
    } else {
      dispatch(closed());
    }
  };

  if (formLoading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={PaymentReceiptDetail}
      render={(formRenderProps: FormRenderProps) => (
        <FormElement style={{ width: "100%" }}>
          <GridLayout
            style={{
              display: "flex",
              flexWrap: "wrap",
            }}
            gap={{ rows: 10 }}
            cols={[{ width: "100%" }]}
          >
            <GridLayoutItem>
              <Typography.h4 className="m-0">
                {payment_guid
                  ? "Update Payment Receipt"
                  : "Add Payment Receipt"}
              </Typography.h4>
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="receipt_date"
                label="Receipt Date"
                component={FormDatePicker}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="karigar_id"
                label="Karigar"
                placeholder="Select Karigar"
                component={FormSelectionField}
                options={KarigarList?.map((karigar: IKarigar) => {
                  return {
                    value: karigar?.id,
                    label: `${karigar?.karigar_name}`,
                  };
                })}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="amount"
                label="Amount"
                component={FormNumericTextField}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem style={{ width: "100%" }}>
              <Field
                name="remarks"
                label="Remarks"
                component={FormTextArea}
                validator={requiredValidator}
                astrike={true}
              />
            </GridLayoutItem>
            <GridLayoutItem
              colSpan={3}
              style={{
                textAlign: "end",
                marginTop: "10px",
                width: "100%",
              }}
            >
              <ButtonWithLoading
                label={payment_guid ? "Update" : "Save"}
                type="submit"
                disabled={!formRenderProps.allowSubmit || formLoading}
                loading={formLoading}
              />
              <Button
                type="button"
                fillMode={"outline"}
                themeColor={"primary"}
                style={{ marginLeft: 4 }}
                onClick={handleFormClose}
              >
                Cancel
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </FormElement>
      )}
    />
  );
};

export default CreatePaymentReceipt;
